"use client";

import { cn } from "@health/ui/utils";
import { ArrowUpIcon, ShieldCheckIcon } from "lucide-react";
import type { ComponentProps, SubmitEvent } from "react";

export function ChatInput({
  children,
  className,
  name = "chat-user-input",
  onMessageSubmit,
  disabled,
  activeFormContext,
  ...props
}: ComponentProps<"input"> & {
  onMessageSubmit: (value: string) => void;
  activeFormContext?: boolean;
}) {
  const handleMessageSubmit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const message = new FormData(e.target).get(name) as string;

    // Allows empty submissions when form is active (for widget values)
    if (!activeFormContext && (typeof message !== "string" || !message.trim())) {
      return;
    }

    onMessageSubmit(typeof message === "string" ? message.trim() : "");
    e.target.reset();
  };

  return (
    <form onSubmit={handleMessageSubmit}>
      <div
        className={cn(
          "flex items-center gap-3 rounded-t-2xl rounded-b-none bg-secondary-s200 p-4",
          className,
        )}
      >
        <div className="flex items-center justify-center">
          <ShieldCheckIcon className="size-5 text-white fill-brand-teal" />
        </div>
        <input
          name={name}
          autoComplete="off"
          className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-text-light md:text-base"
          {...props}
        />
        <button
          type="submit"
          disabled={disabled}
          title={disabled ? "Wait for response to finish" : undefined}
          className={cn(
            "flex h-12 w-13 items-center justify-center rounded-full bg-accent text-white transition hover:brightness-120 cursor-pointer",
            disabled && "pointer-events-auto cursor-not-allowed opacity-40",
          )}
        >
          <ArrowUpIcon className="size-5" />
        </button>
      </div>
    </form>
  );
}
